/**
 * Seleção ordenada de lotes para flush/replay.
 */

import { planReplayBatches } from '../replay/replayOrchestrator';
import { pickAdaptiveBatch } from '../batch/adaptiveBatch';
import { sortQueueForReplay } from '../ordering/orderedFlush';
import { getGroupKey, type QueueItem } from './types';
import {
  partitionQueueItems,
  flattenPartitionsInFlushOrder,
} from './partitioning';
import { getUnifiedPressureLevel } from '../runtime/pressureModel';
import { acquireOperationContext } from '../coordination/ownershipGate';
import {
  canSpendEffectiveReplayBudget,
  spendPartitionReplayBudget,
} from './partitionPolicies';
import { getQueuePartition } from './partitioning';
import { canRunReplay } from '../runtime/runtimeMode';
import { recordLockConflict } from '../telemetry';
import { rejectIfInvalid } from '../coordination/ownershipGate';

export function groupEligibleBySession(eligible: QueueItem[]): Map<string, QueueItem[]> {
  const byGroup = new Map<string, QueueItem[]>();
  for (const item of eligible) {
    const key = getGroupKey(item);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key)!.push(item);
  }
  return byGroup;
}

export function selectReplayBatchesForGroup(
  groupItems: QueueItem[],
  options: {
    queuePressure: number;
    networkOnline: boolean;
    circuitOpen: boolean;
  }
): QueueItem[][] {
  if (!canRunReplay()) return [];

  const replayCtx = acquireOperationContext('replay');
  const gate = rejectIfInvalid(replayCtx);
  if (!gate.ok) {
    if (gate.reason === 'fencing_invalid') recordLockConflict();
    return [];
  }

  const filtered = groupItems.filter(i => i.params.type !== 'PAGE_VIEW');
  const partitioned = partitionQueueItems(filtered);
  const ordered = flattenPartitionsInFlushOrder(partitioned);
  const { batches } = planReplayBatches(ordered);
  const now = Date.now();
  const spentPartitions = new Set<string>();
  return batches
    .map(planned => {
      const partition = planned[0] ? getQueuePartition(planned[0]) : 'behavior';
      if (!canSpendEffectiveReplayBudget(partition, now)) return [] as QueueItem[];
      const batch = pickAdaptiveBatch(planned, {
        queuePressure: options.queuePressure ?? getUnifiedPressureLevel(),
        networkOnline: options.networkOnline,
        circuitOpen: options.circuitOpen,
      });
      if (batch.length > 0 && !spentPartitions.has(partition)) {
        spendPartitionReplayBudget(partition);
        spentPartitions.add(partition);
      }
      return batch;
    })
    .filter(batch => batch.length > 0);
}

export function orderItemsForBeaconUnload(items: QueueItem[]): QueueItem[] {
  return sortQueueForReplay(items.filter(i => i.status !== 'sent'));
}
